import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

export type MIGSMerchantManagerOperationPrivilegeFormValues = {
  maintenanceType?: string;
  justification?: string;
  mso?: string;
  operatorID?: string;
  operatorName?: string;
  staffID?: string;
  workingLocation?: string;
  description?: string;
  emailAddress?: string;
  locale?: string;
  timezone?: string;
  changeOwnPassword?: boolean;
  nextLogin?: boolean;
  merchantProfileView?: boolean;
  viewMerchant?: boolean;
  viewGateway?: boolean;
  download?: boolean;
  others?: string;
};

type Key = keyof MIGSMerchantManagerOperationPrivilegeFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: MIGSMerchantManagerOperationPrivilegeFormValues) => void;
  open: boolean;
  values: Partial<MIGSMerchantManagerOperationPrivilegeFormValues>;
  readonly?: boolean;
};

export const MIGSMerchantManagerOperationPrivilegeForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<MIGSMerchantManagerOperationPrivilegeFormValues>(
    {
      maintenanceType: undefined,
      justification: undefined,
      mso: undefined,
      operatorID: undefined,
      operatorName: undefined,
      staffID: undefined,
      workingLocation: undefined,
      description: undefined,
      emailAddress: undefined,
      locale: undefined,
      timezone: undefined,
      changeOwnPassword: undefined,
      nextLogin: true,
      merchantProfileView: undefined,
      viewMerchant: undefined,
      viewGateway: undefined,
      download: undefined,
      others: undefined,
      ...values,
    }
  );

  function onFormValueChange(
    name: Key,
    value: MIGSMerchantManagerOperationPrivilegeFormValues[typeof name]
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate(
    name: Key,
    value: MIGSMerchantManagerOperationPrivilegeFormValues[typeof name]
  ): Promise<[Key, Error?]> {
    switch (name) {
      case "maintenanceType":
      case "justification":
      case "description":
      case "emailAddress":
      case "operatorName":
      case "workingLocation":
        return Promise.resolve([name, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as Key, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);

      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="MIGS Merchant Manager Operation Privilege Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1 - Information</Form.Title>
        <Form.Dropdown
          error={form.errors.maintenanceType}
          label="Maintenance Type"
          name="maintenanceType"
          onChange={onFormValueChange}
          options={[
            { label: "No Selection", value: "" },
            "Add",
            "Change of Privilege",
          ]}
          required
          size={6}
          value={form.values.maintenanceType || ""}
        />
        <Form.LineBreak />
        <Form.Textarea
          error={form.errors.justification}
          label="Justification"
          maxLength={1000}
          name="justification"
          onChange={onFormValueChange}
          required
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.justification}
        />
        <Form.Title>2 - Operator Details</Form.Title>
        <Form.Textbox
          label={{
            text: "MSO (optional)",
            tooltip: "e.g. HSBC, HSBCCBH, HSBCMLD, HSBCBSB, HSBCSGH, HSBCMNL",
          }}
          name="mso"
          onChange={onFormValueChange}
          size={6}
          value={form.values.mso}
        />
        <Form.Textbox
          error={form.errors.description}
          label={{
            text: "Description",
            tooltip: "e.g. Manager, Supervisor, Operator, Helpdesk",
          }}
          name="description"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.description}
        />
        <Form.Textbox
          label={{
            text: "Operator ID (optional)",
            tooltip:
              "Existing MIGS / CyberSource Portal operator ID For new operator : leave this field blank",
          }}
          name="operatorID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.operatorID}
        />
        <Form.Textbox
          error={form.errors.emailAddress}
          label="Email Address"
          name="emailAddress"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.emailAddress}
        />
        <Form.Textbox
          error={form.errors.operatorName}
          label={{
            text: "Operator Name",
            tooltip:
              "Staff Full Name (Last name in capital letter, then first name)",
          }}
          name="operatorName"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.operatorName}
        />
        <Form.Textbox
          label="Locale (optional)"
          name="locale"
          onChange={onFormValueChange}
          size={6}
          value={form.values.locale}
        />
        <Form.Textbox
          label={{
            text: "Staff ID (optional)",
            tooltip:
              "For GPAP staff, default to start with 'GP + Working Location'",
          }}
          name="staffID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.staffID}
        />
        <Form.Textbox
          label="TimeZone (optional)"
          name="timezone"
          onChange={onFormValueChange}
          size={6}
          value={form.values.timezone}
        />
        <Form.Dropdown
          error={form.errors.workingLocation}
          label={{
            text: "Working Location",
            tooltip: "For GPAP staff only e.g. Hong Kong, Canada",
          }}
          name="workingLocation"
          onChange={onFormValueChange}
          options={[
            { label: "No Selection", value: "" },
            "Hong Kong",
            "Philippines",
          ]}
          required
          size={6}
          value={form.values.workingLocation || ""}
        />
        <Form.Title>3 - For Add/Change of Privilege Only</Form.Title>
        <Form.Group label="Security Options">
          <Form.Checkbox
            label={{
              text: "Change own password",
              tooltip: "Default privilege for each user",
            }}
            name="changeOwnPassword"
            onChange={onFormValueChange}
            size={12}
            value={form.values.changeOwnPassword}
          />
          <Form.Checkbox
            disabled
            label={{
              text: "Must change password at next login",
              tooltip: "For security reason, this box must be checked",
            }}
            name="nextLogin"
            onChange={onFormValueChange}
            size={12}
            value={form.values.nextLogin}
          />
        </Form.Group>
        <Form.Group label="Merchant Profile">
          <Form.Checkbox
            label={{
              text: "View merchant profile",
              tooltip: "Optional Field",
            }}
            name="merchantProfileView"
            onChange={onFormValueChange}
            size={12}
            value={form.values.merchantProfileView}
          />
        </Form.Group>
        <Form.Group label="Transactions">
          <Form.Checkbox
            label={{
              text: "View merchant transaction details reports",
              tooltip: "Optional Field",
            }}
            name="viewMerchant"
            onChange={onFormValueChange}
            size={12}
            value={form.values.viewMerchant}
          />
          <Form.Checkbox
            label={{
              text: "View gateway reports",
              tooltip: "Optional Field",
            }}
            name="viewGateway"
            onChange={onFormValueChange}
            size={12}
            value={form.values.viewGateway}
          />
          <Form.Checkbox
            label={{
              text: "Download transaction result",
              tooltip: "Optional Field",
            }}
            name="download"
            onChange={onFormValueChange}
            size={12}
            value={form.values.download}
          />
        </Form.Group>
        <Form.Textarea
          label="Others"
          maxLength={200}
          name="others"
          onChange={onFormValueChange}
          placeholder="Write details here. Max 200 characters"
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.others}
        />
      </Form>
    </Wrapper>
  );
};
